<template>
  <v-card class="mb-4" style="height: 460px;">
    <v-card-title class="d-flex justify-space-between align-center">
      {{ $t('documentTypes') }}
      <v-btn-toggle
        v-model="viewMode"
        mandatory
        dense
      >
        <v-btn value="count">{{ $t('count') }}</v-btn>
        <v-btn value="size">{{ $t('size') }}</v-btn>
      </v-btn-toggle>
    </v-card-title>
    <v-card-text>
      <v-chart class="chart" ref="chartRef" :option="chartOption" />
    </v-card-text>
  </v-card>
</template>

<script setup>
import { ref, watch, onMounted, defineProps, computed } from 'vue';
import { use } from "echarts/core";
import { useStore } from 'vuex';
import { CanvasRenderer } from "echarts/renderers";
import { BarChart } from "echarts/charts";
import { TooltipComponent, TitleComponent, GridComponent } from "echarts/components";
import VChart from "vue-echarts";

use([CanvasRenderer, BarChart, TooltipComponent, TitleComponent, GridComponent]);

const props = defineProps({
  documents: {
    type: Array,
    required: true
  }
});

const store = useStore();
const chartColor = computed(() => store.getters.chartFontColor);


const chartRef = ref(null);
const chartOption = ref({});
const viewMode = ref('count');

const formatFileSize = (bytes) => {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};

const updateChartOption = () => {
  const fileTypes = {};
  props.documents.forEach(doc => {
    if (doc.fileType) {
      if (!fileTypes[doc.fileType]) {
        fileTypes[doc.fileType] = { count: 0, size: 0 };
      }
      fileTypes[doc.fileType].count++;
      fileTypes[doc.fileType].size += doc.fileSize || 0;
    }
  });

  const sortedData = Object.entries(fileTypes).sort((a, b) => a[0].localeCompare(b[0]));
  const names = sortedData.map(([name]) => name);
  let values, yAxisLabel;

  if (viewMode.value === 'count') {
    values = sortedData.map(([, data]) => data.count);
    yAxisLabel = 'Count';
  } else {
    values = sortedData.map(([, data]) => data.size);
    yAxisLabel = 'Total Size';
  }

  chartOption.value = {
    tooltip: {
      trigger: 'axis',
      axisPointer: {
        type: 'shadow'
      },
      confine: true,
      enterable: true,
      extraCssText: 'max-width: 320px; overflow-x: auto;',
      formatter: function(params) {
        const dataIndex = params[0].dataIndex;
        const fileType = names[dataIndex];
        const value = params[0].value;
        if (viewMode.value === 'count') {
          return `${fileType}: ${value}`;
        } else {
          return `${fileType}: ${formatFileSize(value)}`;
        }
      }
    },
    grid: {
      left: '3%',
      right: '4%',
      bottom: '3%',
      containLabel: true
    },
    xAxis: {
      type: 'category',
      data: names,
      axisTick: {
        alignWithLabel: true
      },
      axisLabel: {
        color: chartColor.value,
        rotate: 45,
        interval: 0
      }
    },
    yAxis: {
      type: 'value',
      name: yAxisLabel,
      axisLabel: {
        color: chartColor.value,
        formatter: function(value) {
          return viewMode.value === 'size' ? formatFileSize(value) : value;
        }
      }
    },
    series: [
      {
        name: 'Document Types',
        type: 'bar',
        data: values,
        itemStyle: {
          borderRadius: [5, 5, 0, 0],
          color: function(params) {
            const colorList = ['#5470c6', '#91cc75', '#fac858', '#ee6666', '#73c0de', '#3ba272', '#fc8452', '#9a60b4'];
            return colorList[params.dataIndex % colorList.length];
          }
        },
        label: {
          show: true,
          position: 'top',
          formatter: function(params) {
            return viewMode.value === 'size' ? formatFileSize(params.value) : params.value;
          },
          textStyle: {
            color: chartColor.value
          }
        }
      }
    ]
  };
};

watch(() => props.documents, () => {
  updateChartOption();
}, { deep: true });

watch(viewMode, () => {
  updateChartOption();
});

watch(() => chartColor.value, () => {
  updateChartOption();
});

onMounted(() => {
  updateChartOption();
  window.addEventListener('resize', () => {
    if (chartRef.value) {
      chartRef.value.resize();
    }
  });
});
</script>

<style scoped>
.chart {
  height: 400px;
}
</style>